import { useEffect, useRef, useState } from 'react';
import { engine, useEngine } from '../hooks/useEngine.ts';
import { formatDuration, useTicker } from '../hooks/useTicker.ts';
import { ChatDock } from './ChatDock.tsx';
import { ControlBar } from './ControlBar.tsx';
import { Icebreaker } from './Icebreaker.tsx';
import { LocalPip } from './LocalPip.tsx';
import { ReactionLayer } from './ReactionLayer.tsx';
import { ReunionOverlay } from './Reunion.tsx';
import { SearchingOverlay } from './SearchingOverlay.tsx';
import { SparkOverlay } from './SparkOverlay.tsx';
import { StayPrompt } from './StayPrompt.tsx';
import { HeartIcon, HourglassIcon } from './Icons.tsx';

const QUALITY_STYLES = {
  unknown: 'bg-white/30',
  great: 'bg-emerald-400',
  ok: 'bg-amber-400',
  poor: 'bg-red-500',
} as const;

export function CallStage() {
  const state = useEngine();
  const remoteRef = useRef<HTMLVideoElement>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const touchStart = useRef<{ x: number; y: number; at: number } | null>(null);

  useTicker(state.phase === 'live' || state.phase === 'searching');

  // Attach the remote stream and cope with autoplay policies (iOS especially).
  useEffect(() => {
    const video = remoteRef.current;
    if (!video) return;
    if (video.srcObject !== state.remoteStream) video.srcObject = state.remoteStream;
    if (!state.remoteStream) return;

    video
      .play()
      .then(() => setNeedsTap(false))
      .catch(() => setNeedsTap(true));
  }, [state.remoteStream]);

  // Keyboard shortcuts for desktop power users.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          chatOpen ? setChatOpen(false) : engine.stop();
          break;
        case ' ':
        case 'ArrowRight':
          event.preventDefault();
          engine.next();
          break;
        case 'm':
          engine.toggleMic();
          break;
        case 'v':
          engine.toggleCam();
          break;
        case 'l':
          engine.like();
          break;
        case 'c':
          event.preventDefault();
          setChatOpen((value) => !value);
          break;
        case 's':
          engine.stay();
          break;
        case 't':
          engine.newPrompt();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatOpen]);

  // Swipe up anywhere on the video to skip — the core mobile gesture.
  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY, at: Date.now() };
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    touchStart.current = null;
    if (!start || !touch) return;

    const dy = start.y - touch.clientY;
    const dx = Math.abs(start.x - touch.clientX);
    if (dy > 90 && dx < 70 && Date.now() - start.at < 700) engine.next();
  };

  const elapsed = state.callStartedAt ? (Date.now() - state.callStartedAt) / 1000 : 0;
  const peerName = state.peer?.name ?? 'Stranger';
  const searching = state.phase === 'searching' || state.phase === 'connecting';

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-ink-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <video
        ref={remoteRef}
        autoPlay
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Gradient scrims keep the overlaid UI readable over any video. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-black/80 to-transparent" />

      {/* Header */}
      <header
        className="absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-3 p-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="flex flex-col items-start gap-2">
          <img src="/logo.png" alt="Phidio" className="h-6 w-auto opacity-70" />

          {state.phase === 'live' && (
            <div
              className={`glass flex items-center gap-2.5 rounded-full py-1.5 pl-3 pr-3.5 text-sm ${
                state.stayed ? 'ring-1 ring-amber-300/50' : ''
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${QUALITY_STYLES[state.quality]}`} />
              <span className="font-medium">{peerName}</span>
              {state.peer?.country && (
                <span className="text-white/40">{flagOf(state.peer.country)}</span>
              )}
              <span
                className={`tabular-nums ${state.stayed ? 'font-semibold text-amber-300' : 'text-white/50'}`}
              >
                {formatDuration(elapsed)}
              </span>
              {state.stayed && (
                <span
                  className="text-amber-300"
                  title="You both chose to stay"
                  aria-label="You both chose to stay"
                >
                  <HourglassIcon width={14} height={14} />
                </span>
              )}
            </div>
          )}

          {state.common.length > 0 && state.phase === 'live' && (
            <ul className="flex flex-wrap gap-1.5">
              {state.common.map((tag) => (
                <li
                  key={tag}
                  className="rounded-full bg-phidio-500/25 px-2.5 py-1 text-[11px] font-medium text-phidio-100 ring-1 ring-phidio-500/40"
                >
                  #{tag}
                </li>
              ))}
            </ul>
          )}
        </div>

        {state.likedByPeer && !state.spark && (
          <div className="glass flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-phidio-200">
            <HeartIcon filled width={16} height={16} />
            They liked you
          </div>
        )}
      </header>

      <LocalPip stream={state.localStream} mirror={state.profile.mirror} camOn={state.camOn} />

      <ReactionLayer reactions={state.reactions} />

      {needsTap && state.remoteStream && (
        <button
          type="button"
          onClick={() => {
            void remoteRef.current?.play();
            setNeedsTap(false);
          }}
          className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 text-lg font-medium"
        >
          Tap to start the video
        </button>
      )}

      {searching &&
        (state.reconnect?.direction === 'outgoing' ? (
          <ReunionOverlay name={state.reconnect.name} />
        ) : (
          <SearchingOverlay
            interests={state.profile.interests}
            strict={state.profile.strict}
            stats={state.serverStats}
            searchStartedAt={state.searchStartedAt}
          />
        ))}

      {state.spark && <SparkOverlay peerName={peerName} />}

      <ChatDock
        open={chatOpen}
        messages={state.messages}
        peerName={peerName}
        peerTyping={state.peerTyping}
        onClose={() => setChatOpen(false)}
      />

      <ControlBar
        micOn={state.micOn}
        camOn={state.camOn}
        soundOn={state.profile.soundOn}
        canFlipCamera={state.canFlipCamera}
        liked={state.liked}
        inCall={state.phase === 'live'}
        unread={state.unread}
        chatOpen={chatOpen}
        hasPrompt={!!state.prompt}
        promptOpen={state.promptOpen}
        stayInvited={state.stayInvited}
        stayed={state.stayed}
        confirmNext={state.confirmNext}
        onToggleChat={() => setChatOpen((value) => !value)}
      >
        <StayPrompt
          peerName={peerName}
          elapsed={elapsed}
          live={state.phase === 'live'}
          asked={state.stayAsked}
          invited={state.stayInvited}
          stayed={state.stayed}
        />
        <Icebreaker
          prompt={state.prompt}
          open={state.promptOpen}
          live={state.phase === 'live'}
        />
      </ControlBar>

      {state.phase === 'live' && (
        <p className="pointer-events-none absolute inset-x-0 bottom-1 z-10 hidden text-center text-[11px] text-white/25 sm:block">
          space / → next · m mute · v camera · l like · s stay · t topic · c chat · esc leave
        </p>
      )}
    </div>
  );
}

/** Turns an ISO-3166 alpha-2 code into its flag emoji. */
function flagOf(code: string): string {
  if (code.length !== 2) return '';
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65),
  );
}
